import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WhatsAppEmbeddedSignup } from '@/components/WhatsAppEmbeddedSignup';
import { API_CONFIG } from '@/lib/api/config';

import { friendlyMessage } from '@/lib/errors';
type Stage = 'verifying' | 'ready' | 'done' | 'error';

export default function WaConnect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    const redirect = searchParams.get('redirect');

    if (!token) {
      setErrorMessage('No login token found. Please request a new link by messaging us on WhatsApp.');
      setStage('error');
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_CONFIG.BASE_URL}/auth/wa-link/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          setErrorMessage(friendlyMessage(data.error, 'This link has expired or is invalid. Please message us on WhatsApp to get a new one.'));
          setStage('error');
          return;
        }

        localStorage.setItem('auth_token', data.data.token);
        if (data.data.refreshToken) localStorage.setItem('refresh_token', data.data.refreshToken);

        if (redirect && redirect.startsWith('/')) {
          navigate(redirect, { replace: true });
          return;
        }

        setStage('ready');
      } catch {
        setErrorMessage('Failed to verify the link. Please check your connection and try again.');
        setStage('error');
      }
    })();
  }, [searchParams, navigate]);

  const handleSuccess = () => {
    setStage('done');
    setTimeout(() => navigate('/dashboard'), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500 mb-4">
            <Smartphone className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Connect WhatsApp Business</h1>
          <p className="text-gray-500 mt-1 text-sm">
            One last step — link your WhatsApp Business number to start managing conversations
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {stage === 'verifying' && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
              <p className="text-gray-600 text-sm">Signing you in…</p>
            </div>
          )}

          {stage === 'ready' && (
            <div className="space-y-4">
              <WhatsAppEmbeddedSignup
                onSuccess={handleSuccess}
                onError={(msg) => setErrorMessage(msg)}
                autoActivateOnboarding={true}
              />
              {errorMessage && (
                <p className="text-sm text-red-500 text-center">{errorMessage}</p>
              )}
              <Button
                variant="ghost"
                className="w-full text-gray-400 text-sm"
                onClick={() => navigate('/dashboard')}
              >
                I'll do this later
              </Button>
            </div>
          )}

          {stage === 'done' && (
            <div className="flex flex-col items-center gap-3 py-8">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              <p className="text-gray-900 font-medium">WhatsApp Business connected!</p>
              <p className="text-gray-500 text-sm">Taking you to your dashboard…</p>
            </div>
          )}

          {stage === 'error' && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <AlertCircle className="w-10 h-10 text-red-400" />
              <p className="text-gray-900 font-medium">This link has expired</p>
              <p className="text-gray-500 text-sm max-w-xs">{errorMessage}</p>
              <p className="text-gray-400 text-xs max-w-xs mt-1">
                Message us on WhatsApp to get a fresh link, or sign in directly.
              </p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => navigate('/auth')}>Sign in</Button>
                <Button onClick={() => navigate('/dashboard')}>Go to dashboard</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
